import React, { useState, useEffect } from "react";
import {
  AppLayout,
  BreadcrumbGroup,
  Flashbar,
  SideNavigation,
  TopNavigation,
  Spinner,
  Box,
  Button,
} from "@cloudscape-design/components";
import { useAuth } from "./auth/useAuth";
import { MaintenanceSchedule } from "./pages/MaintenanceSchedule";
import { Information } from "./pages/Information";
import { CallMinutes } from "./pages/CallMinutes";
import { SuperAdmin } from "./pages/SuperAdmin";
import { SiteAdmin } from "./pages/SiteAdmin";
import { apiClient } from "./api/client";

type Page = "schedule" | "info" | "minutes" | "siteadmin" | "admin";

const App: React.FC = () => {
  const { user, isLoading, isAuthenticated, login, logout } = useAuth();
  const [activePage, setActivePage] = useState<Page>("schedule");
  const [notifications, setNotifications] = useState<any[]>([]);
  const [currentSite, setCurrentSite] = useState<string>(user?.site || "CWL1");
  const [availableSites, setAvailableSites] = useState<string[]>([]);

  const isAdmin = user?.role === "site-admin" || user?.role === "superadmin";

  useEffect(() => {
    if (user) {
      setCurrentSite(user.site || "CWL1");
      // Fetch available sites
      apiClient.get("/sites").then((data: any) => {
        const sites = (data.sites || []).map((s: any) => s.siteId);
        setAvailableSites(sites);
      }).catch(() => {
        setAvailableSites([user.site || "CWL1"]);
      });
    }
  }, [user]);

  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: "xxxl" }}>
        <Spinner size="large" />
        <Box variant="p" padding={{ top: "s" }}>Loading...</Box>
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <Box textAlign="center" padding={{ top: "xxxl" }}>
        <Box variant="h1">RME Maintenance Planner</Box>
        <Box variant="p" padding={{ top: "s", bottom: "l" }}>
          Sign in with your Amazon credentials to continue.
        </Box>
        <Button variant="primary" onClick={login}>Sign In with Federate</Button>
      </Box>
    );
  }

  const navItems: any[] = [
    { type: "link", text: "Maintenance Schedule", href: "#schedule" },
    { type: "link", text: "Information", href: "#info" },
    { type: "link", text: "Call Minutes", href: "#minutes" },
  ];

  if (isAdmin) {
    navItems.push({ type: "divider" });
    navItems.push({ type: "link", text: "Site Admin", href: "#siteadmin" });
  }

  if (user.isSuperadmin) {
    navItems.push({ type: "link", text: "Super Admin", href: "#admin" });
  }

  const renderPage = () => {
    switch (activePage) {
      case "schedule": return <MaintenanceSchedule user={user} siteId={currentSite} />;
      case "info": return <Information user={user} siteId={currentSite} />;
      case "minutes": return <CallMinutes user={user} siteId={currentSite} />;
      case "siteadmin": return isAdmin ? <SiteAdmin user={user} siteId={currentSite} /> : null;
      case "admin": return user.isSuperadmin ? <SuperAdmin user={user} siteId={currentSite} /> : null;
      default: return <MaintenanceSchedule user={user} siteId={currentSite} />;
    }
  };

  const pageLabel = (page: Page) => {
    switch (page) {
      case "schedule": return "Schedule";
      case "info": return "Information";
      case "minutes": return "Call Minutes";
      case "siteadmin": return "Site Admin";
      case "admin": return "Super Admin";
    }
  };

  return (
    <>
      <TopNavigation
        identity={{
          href: "#",
          title: "RME Maintenance Planner",
          logo: { src: "/amazon-logo.svg", alt: "Amazon" },
        }}
        utilities={[
          {
            type: "menu-dropdown",
            text: currentSite,
            iconName: "share",
            items: availableSites.map(s => ({ id: s, text: s })),
            onItemClick: ({ detail }) => {
              setCurrentSite(detail.id);
            },
          },
          {
            type: "menu-dropdown",
            text: user.displayName,
            description: `${user.alias} | ${user.site} | ${user.role}`,
            iconName: "user-profile",
            items: [
              { id: "site", text: `Site: ${user.site}` },
              { id: "role", text: `Role: ${user.role}` },
              { id: "signout", text: "Sign out" },
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === "signout") logout();
            },
          },
        ]}
      />
      <AppLayout
        navigation={
          <SideNavigation
            header={{ text: currentSite, href: "#" }}
            activeHref={`#${activePage}`}
            items={navItems}
            onFollow={(e) => {
              e.preventDefault();
              const page = e.detail.href.replace("#", "") as Page;
              setActivePage(page);
            }}
          />
        }
        notifications={<Flashbar items={notifications} />}
        content={renderPage()}
        toolsHide={true}
        breadcrumbs={
          <BreadcrumbGroup items={[
            { text: "RME Maintenance", href: "#" },
            { text: pageLabel(activePage), href: "#" },
          ]} />
        }
      />
    </>
  );
};

export default App;
